import { useMemo } from "react";
import Button from "ui/buttons/Button";
import Capsule from "ui/components/Capsule";
import { planLibraryActions } from "./planLibraryReducer";
import { usePlanLibraryDispatch, usePlanLibrarySelector } from "./planLibraryStore";
import type { PlanLibraryOptionSet } from "./planLibraryTypes";

interface PlanLibraryRuleBadgeProps {
  optionId?: string;
  optionSet: PlanLibraryOptionSet;
}

export function PlanLibraryRuleBadge({ optionId, optionSet }: PlanLibraryRuleBadgeProps) {
  const dispatch = usePlanLibraryDispatch();
  const { optionRuleRefsByOptionSets } = usePlanLibrarySelector((state) => ({
    optionRuleRefsByOptionSets: state.optionRuleRefsByOptionSets,
  }));

  const ruleIds = useMemo(() => {
    const ids: string[] = [];
    // find all rules for the specified option set / option
    for (const ruleOptionId in optionRuleRefsByOptionSets[optionSet.id] ?? {}) {
      if (optionId && ruleOptionId !== optionId) {
        continue;
      }
      ids.push(...Object.keys(optionRuleRefsByOptionSets[optionSet.id][ruleOptionId]));
    }
    return ids;
  }, []);

  const ruleCount = ruleIds.length;
  return (
    <>
      {ruleCount > 0 && (
        <Capsule
          label={`${ruleCount} rule${ruleCount > 1 ? "s" : ""}`}
          size="small"
          backgroundColor="amethystLightX3"
          onClick={(e) => {
            // stopPropagation to prevent popping the edit option modal
            e.stopPropagation();
            dispatch(
              planLibraryActions.toggleOptionRuleModal({
                editOptionRules: {
                  ruleIds,
                  optionSetId: optionSet?.id,
                  optionKey: optionId,
                },
              }),
            );
          }}
        />
      )}
    </>
  );
}

export function PlanLibraryEditRuleButton() {
  const dispatch = usePlanLibraryDispatch();
  const { optionRuleRefsByOptionSets } = usePlanLibrarySelector((state) => ({
    optionRuleRefsByOptionSets: state.optionRuleRefsByOptionSets,
  }));

  const ruleIds = useMemo(() => {
    const ids: string[] = [];
    for (const ruleRefs of Object.values(optionRuleRefsByOptionSets)) {
      for (const ruleIds of Object.values(ruleRefs)) {
        ids.push(...Object.keys(ruleIds));
      }
    }
    return [...new Set(ids)];
  }, []);

  return (
    <>
      {ruleIds.length > 0 && (
        <Button
          btnStyle="secondary"
          btnSize="medium"
          onClick={(e) => {
            // stopPropagation to prevent popping the edit option modal
            e.stopPropagation();
            dispatch(
              planLibraryActions.toggleOptionRuleModal({
                editOptionRules: {
                  ruleIds,
                },
              }),
            );
          }}
        >
          Edit Option Rules
        </Button>
      )}
    </>
  );
}
